/**
 * useConversationDetail.ts — fetch + refetch lifecycle for a single
 * ConversationDetail (story E0-BO-CONV8).
 *
 * Mirrors the lifecycle of `useLeadDetail`: phases are loading / ready /
 * notFound / error / unauthorized. A stable `refetch` callback is exposed so
 * the page can re-load after a successful control action. Race-safety: a
 * monotonic request id means only the LATEST request may commit; a response
 * resolving after unmount is a no-op.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { ApiError, UnauthorizedError } from '../../api/client';
import type { ConversationDetail } from '../../api/types';
import floating from '../../lib/floating';

export type ConversationDetailPhase = 'loading' | 'ready' | 'notFound' | 'error' | 'unauthorized';

export interface ConversationDetailState {
  detail: ConversationDetail | null;
  phase: ConversationDetailPhase;
  /** Re-run the latest fetch (used after a successful control action). */
  refetch: () => void;
}

export const useConversationDetail = (
  apiClient: ApiClient,
  threadId: string,
): ConversationDetailState => {
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [phase, setPhase] = useState<ConversationDetailPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetch = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const data = await apiClient.getConversationDetail(threadId);
      if (!isCurrent()) return;
      setDetail(data);
      setPhase('ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      if (error instanceof ApiError && error.status === 404) {
        setPhase('notFound');
        return;
      }
      setPhase('error');
    }
  }, [apiClient, threadId]);

  // Initial load on mount or when threadId changes.
  useEffect(() => {
    floating(fetch());
  }, [fetch]);

  return { detail, phase, refetch: fetch };
};
